import React, { useState, FC } from "react";
import { Form, Button, Typography, Row, Col, Space } from "antd";
import { InputItem, DatePickerItem } from "../../../components/forms/FormItems";
import {
  SettingsDataSelect,
  EmployeeEffectiveOrderSelect,
} from "../../../components/forms/AutoCompleteFormItem";
import { useLocation, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loadSalaryLines, createRecord } from "../salarySlice";
import FormHeaderWrapper from "../../../components/forms/FormHeader.style";
import { useIntl } from "react-intl";
import { useSalary_symbols } from "../../../hooks";
import { isAllowedFeature } from "../../organization/organizationSlice";
import {
  CalculatorOutlined,
  CheckSquareOutlined,
  ScheduleOutlined,
} from "@ant-design/icons";
import OrderPlanModal from "./OrderPlanModal";
import AnalysisHoursModal from "./AnalysisHoursModal";
import moment, { Moment } from "moment";
import {
  RootState,
  SalaryLineFormProps,
  SalaryLineFormValues,
  SalaryLineParams,
  SalarySymbol,
} from "../types";

const { Title } = Typography;

interface RouteParams {
  branchId: string;
}

/**
 * SalaryLineForm Component
 *
 * Provides an interface for creating and managing salary line items for an employee.
 * Features include:
 * - Adding salary symbols with values and counts
 * - Setting retroactive periods for salary adjustments
 * - Linking salary entries to orders (when accounting_tag feature is enabled)
 * - Viewing employee work plans and analysis
 * - Recalculating and approving salary entries
 *
 * The form adapts based on the selected salary symbol type:
 * - Type 1: Shows both unit value and unit count fields
 * - Other types: Shows only the sum field
 *
 * @example
 * ```tsx
 * <SalaryLineForm
 *   record={employeeRecord}
 *   onRecalculate={handleRecalculate}
 *   onApprove={handleApprove}
 *   isApproved={false}
 *   fromSalary={true}
 *   onlaunchApprovalModal={handleLaunchApprovalModal}
 * />
 * ```
 */
const SalaryLineForm: FC<SalaryLineFormProps> = ({
  record,
  onRecalculate,
  onApprove,
  employeeId,
  isApproved,
  fromSalary,
  onlaunchApprovalModal,
}) => {
  const [form] = Form.useForm();
  const intl = useIntl();
  const { branchId } = useParams<RouteParams>();
  const location = useLocation();
  const dispatch = useDispatch<any>();
  const [activeSymbol, setActiveSymbol] = useState<SalarySymbol | null>(null);
  const [formValues, setFormValues] = useState<SalaryLineFormValues>({});
  const [isPlanVisible, setIsPlanVisible] = useState<boolean>(false);
  const [isAnalysisVisible, setIsAnalysisVisible] = useState<boolean>(false);

  // Selectors
  const createLoading = useSelector(
    (state: RootState) => state.pendingActions["salaryLine/create"]
  );
  const activeYear = useSelector((state: RootState) => state.salary.activeYear);
  const activeMonth = useSelector(
    (state: RootState) => state.salary.activeMonth
  );
  const activeBranch = useSelector(
    (state: RootState) => state.salary.activeBranch
  );
  const salarySymbols = useSalary_symbols();
  const isAdmin = location.pathname.includes("admin");

  /**
   * Handles form submission to create a new salary line.
   *
   * @param {SalaryLineFormValues} values - The form values to submit
   */
  const handleSubmit = async (values: SalaryLineFormValues) => {
    const params = {
      branchId: isAdmin ? activeBranch : branchId,
      employeeId: record.id,
      salaryLine: {
        symbol_id: values.symbol_id,
        unit_count:
          activeSymbol?.type === 1 ? values.unit_count : values.unit_sum,
        unit_value: values.unit_value,
        year: +activeYear,
        month: +activeMonth,
        employee_id: record.id,
        order_id: values.order_id,
        retro_year:
          values.retro_period && +moment(values.retro_period).format("YYYY"),
        retro_month:
          values.retro_period && +moment(values.retro_period).format("M"),
      } as SalaryLineParams,
    };

    const action = await dispatch(createRecord(params));
    if (!action.error) {
      form.resetFields();
      setActiveSymbol(null);
      dispatch(loadSalaryLines(isAdmin, record.id));
    }
  };

  /**
   * Handles selection change of the salary symbol.
   * Updates the activeSymbol state which determines which form fields to display.
   *
   * @param {string | number} selected - The selected salary symbol ID
   */
  const handleSymbolChange = (selected: string | number) => {
    const symbol = salarySymbols.find(
      (item: SalarySymbol) => item.id === selected
    );
    setActiveSymbol(symbol || null);
  };

  /**
   * Triggers salary recalculation and reloads salary lines.
   */
  const handleRecalculate = async () => {
    await onRecalculate(employeeId);
    dispatch(loadSalaryLines(isAdmin, employeeId));
  };

  /**
   * Handles form value changes.
   * Updates form state and resets order_id when retro_period changes.
   *
   * @param {SalaryLineFormValues} changed - The changed form values
   * @param {SalaryLineFormValues} all - All current form values
   */
  const handleValuesChange = (
    changed: SalaryLineFormValues,
    all: SalaryLineFormValues
  ) => {
    setFormValues(all);
    if ("retro_period" in changed) {
      form.setFieldValue("order_id", null);
    }
  };

  /**
   * Filters out calculated-only salary symbols from the selection list.
   *
   * @param {SalarySymbol[]} items - The array of salary symbols
   * @returns {SalarySymbol[]} - Filtered array excluding calculated-only symbols
   */
  const filterNonCalculatedItems = (items: SalarySymbol[]) =>
    items.filter((item) => !item.calculated_only);

  /**
   * Determines which dates should be disabled in the date picker.
   * Prevents selecting dates after the end of the active month.
   *
   * @param {Moment} current - The date being evaluated
   * @returns {boolean} - True if date should be disabled, false otherwise
   */
  const disabledDate = (current: Moment) => {
    const activeDate = moment([+activeYear, +activeMonth - 1, 1]);
    return current && current > activeDate.endOf("month");
  };

  /**
   * Toggles visibility of the employee work plan modal.
   */
  const togglePlanModal = () => setIsPlanVisible((prev) => !prev);

  /**
   * Toggles visibility of the hours analysis modal.
   */
  const toggleAnalysisModal = () => setIsAnalysisVisible((prev) => !prev);

  // Common button props for consistent styling
  const buttonProps = {
    size: "middle" as const,
    style: { marginLeft: 8 },
  };

  return (
    <div style={{ width: "100%" }}>
      {/* Header Section */}
      <FormHeaderWrapper>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: "100%",
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            {intl.formatMessage({ id: "app.salary.addSalarySymbol" })}
          </Title>
          <Space>
            <Button
              {...buttonProps}
              onClick={toggleAnalysisModal}
              icon={<CalculatorOutlined />}
            >
              {intl.formatMessage({ id: "app.salary.show_analysis" })}
            </Button>
            <Button
              {...buttonProps}
              onClick={togglePlanModal}
              icon={<ScheduleOutlined />}
            >
              {intl.formatMessage({ id: "app.salary.show_plan" })}
            </Button>
            {fromSalary && (
              <Button
                {...buttonProps}
                onClick={onlaunchApprovalModal}
                icon={<ScheduleOutlined />}
              >
                {intl.formatMessage({ id: "app.scheduling.approval" })}
              </Button>
            )}
            <Button
              {...buttonProps}
              onClick={handleRecalculate}
              icon={<CalculatorOutlined />}
            >
              {intl.formatMessage({ id: "app.salary.recalculate" })}
            </Button>
            {onApprove && (
              <Button
                {...buttonProps}
                type="primary"
                onClick={() => onApprove(isApproved, employeeId)}
                icon={<CheckSquareOutlined />}
              >
                {intl.formatMessage({
                  id: isApproved
                    ? "app.salary.unlock_employee_salary"
                    : "app.salary.approve_employee_salary",
                })}
              </Button>
            )}
          </Space>
        </div>
      </FormHeaderWrapper>

      {/* Form Section */}
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        onValuesChange={handleValuesChange}
        style={{ width: "100%" }}
      >
        <Row gutter={[16, 16]}>
          {/* Salary Symbol Selection */}
          <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
            <SettingsDataSelect
              name="symbol_id"
              category="salary"
              settingsdataName="system_tables/salary/salary_symbols"
              paramName="symbol"
              required
              onChange={handleSymbolChange}
              filterItems={filterNonCalculatedItems}
              style={{ zIndex: 999 }}
            />
          </Col>

          {/* Unit Value - Only for type 1 symbols */}
          {activeSymbol?.type === 1 && (
            <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
              <InputItem
                name="unit_value"
                number
                category="salary"
                twoDigitsNumbers
                onlyPositiveNumbers
              />
            </Col>
          )}

          {/* Unit Count or Sum - Depends on symbol type */}
          {activeSymbol && (
            <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
              <InputItem
                name={activeSymbol.type === 1 ? "unit_count" : "unit_sum"}
                number
                category="salary"
                required
                twoDigitsNumbers
                onlyPositiveNumbers
              />
            </Col>
          )}

          {/* Retro Period Selection */}
          {activeSymbol && (
            <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
              <DatePickerItem
                name="retro_period"
                category="salary"
                disabledDate={disabledDate}
                defaultPickerValue={undefined}
                picker="month"
                format="MM/YYYY"
              />
            </Col>
          )}

          {/* Order Selection - Only when accounting_tag feature is enabled */}
          {isAllowedFeature("accounting_tag") && (
            <Col xs={24} sm={12} md={6} lg={5} xl={5} xxl={4}>
              <EmployeeEffectiveOrderSelect
                name="order_id"
                category="salary"
                required
                identfierId="order_id"
                staleTime={0}
                params={{
                  branchId: isAdmin ? activeBranch : branchId,
                  employeeId: record.id,
                  query: {
                    month: formValues.retro_period
                      ? +moment(formValues.retro_period).format("M")
                      : activeMonth,
                    year: formValues.retro_period
                      ? +moment(formValues.retro_period).format("YYYY")
                      : activeYear,
                  },
                }}
              />
            </Col>
          )}

          {/* Submit Button */}
          <Col xs={24} sm={12} md={6} lg={5} xl={4} xxl={3}>
            <div
              style={{
                minHeight: "63px",
                display: "flex",
                alignItems: "flex-end",
                marginBottom: "16px",
              }}
            >
              <Button
                type="primary"
                htmlType="submit"
                loading={createLoading}
                style={{ width: "100%" }}
              >
                {intl.formatMessage({ id: "app.actions.add_salaryLine" })}
              </Button>
            </div>
          </Col>
        </Row>
      </Form>

      {/* Related Modals */}
      <OrderPlanModal
        employee={record}
        visible={isPlanVisible}
        onHide={togglePlanModal}
        activeYear={+activeYear}
        activeMonth={+activeMonth}
      />
      <AnalysisHoursModal
        employee={record}
        visible={isAnalysisVisible}
        onHide={toggleAnalysisModal}
        activeYear={activeYear}
        activeMonth={activeMonth}
        branch_id={isAdmin && activeBranch !== null ? activeBranch : branchId}
      />
    </div>
  );
};

export default SalaryLineForm;
